import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import { utcToZonedTime, format, formatInTimeZone } from 'date-fns-tz'
import ptBR from 'date-fns/locale/pt-BR'

/**
 * Fuso que define o calendário da plataforma.
 *
 * Liberação de conteúdo é por DIA, e o dia precisa ser o mesmo para o servidor,
 * para o produtor que escolheu a data e para o aluno — em qualquer aparelho, em
 * qualquer lugar. Sem um fuso fixo o corte vira a meia-noite do aparelho: aluno
 * a leste de Brasília destravava a aula antes do servidor e tomava 403 ao abrir.
 *
 * Mesma escolha que o servidor faz em BlockDueTime (services/core) e que o core
 * já usa em certificados, relatórios e e-mails de live.
 */
export const RELEASE_TIME_ZONE = 'America/Sao_Paulo'

function determineTimeZone(dateString: string): string {
  if (dateString.length === 10) return 'UTC'
  return dateString.endsWith('Z') ? 'UTC' : dateString.slice(-6)
}

function formatDate(dateString: string, formatString: string): string {
  if (!dateString) return '-'
  const timeZone = determineTimeZone(dateString)
  const zonedDate = utcToZonedTime(new Date(dateString), timeZone)
  return format(zonedDate, formatString, { locale: ptBR })
}

export function formatDateLong(dateString: string): string {
  return formatDate(dateString, "EEEE, dd 'de' MMMM 'de' yyyy")
}

export function formatDateMedium(dateString: string): string {
  return formatDate(dateString, "dd 'de' MMMM 'de' yyyy")
}

export function formatDateShort(dateString: string): string {
  return formatDate(dateString, 'dd/MM/yyyy')
}

export function formatTime(dateString: string): string {
  return formatDate(dateString, 'HH:mm:ss')
}

export function formatDateComplete(dateString: string): string {
  return formatDate(dateString, "EEEE, dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss")
}

/**
 * Verdadeiro quando o INSTANTE da data já passou.
 *
 * Compara instantes crus, sem utcToZonedTime: o que a função devolve é uma Date
 * cujos campos LOCAIS representam a hora de parede no fuso pedido, então o
 * instante dela está deslocado pelo offset da máquina. Comparar isso com
 * `new Date()` (instante real) errava por esse offset — em Brasília o bloqueio
 * caía 3h depois do servidor, e nessa janela o app oferecia conteúdo que o
 * servidor já negava (403 em authorize_library_resources).
 *
 * Serve para bloqueio (lock_at/lock_after/available_until), que o servidor
 * aplica no instante. Para liberação use isReleaseDayReached.
 */
export function isPastDate(dateString: string): boolean {
  return new Date(dateString) < new Date()
}

/**
 * Dia do calendário em que o conteúdo libera, como 'yyyy-MM-dd'.
 *
 * O dia é lido no fuso que a própria string carrega (Z -> UTC), e não em
 * RELEASE_TIME_ZONE: o produtor escolhe a data num date-picker local e o valor
 * chega como a meia-noite dele em UTC (`03:00Z` na esmagadora maioria da base,
 * `02:00Z` no horário de verão antigo). Nesses valores a data UTC é a data que o
 * produtor escolheu; reinterpretar em Brasília jogaria os registros de `02:00Z`
 * para o dia anterior.
 */
function releaseDayOf(dateString: string): string {
  const timeZone = determineTimeZone(dateString)
  return format(utcToZonedTime(new Date(dateString), timeZone), 'yyyy-MM-dd')
}

/**
 * Verdadeiro quando o DIA da liberação já chegou, ignorando a hora.
 *
 * Liberação de conteúdo é por dia, não por instante: o servidor compara com o
 * início do dia da liberação (Lesson#available_for? e
 * BlockDueTime#block_due_time_constraints?, em services/core), então uma aula
 * que "libera em 17/08" está liberada desde o começo de 17/08.
 *
 * isPastDate compara o instante cheio e por isso não serve aqui: uma aula com
 * liberação calculada para as 15h do dia 17 ficaria travada a manhã inteira,
 * exibindo "Libera em 17/08" no próprio dia 17 — e o servidor já a liberava.
 *
 * O corte é a meia-noite de RELEASE_TIME_ZONE, não a do aparelho. Comparar
 * strings 'yyyy-MM-dd' resolve isso sem aritmética de offset e sobrevive a
 * horário de verão: a pergunta é só "hoje em Brasília já é o dia da liberação?".
 * Antes o corte era `setHours(0,0,0,0)` no fuso da máquina, então cada aluno
 * destravava num instante diferente — a leste de Brasília, antes do servidor.
 *
 * O dia comparado é o mesmo que formatDateShort exibe, então o que o aluno lê e
 * o que o app libera não podem divergir, em nenhum fuso.
 */
export function isReleaseDayReached(dateString: string): boolean {
  const today = formatInTimeZone(new Date(), RELEASE_TIME_ZONE, 'yyyy-MM-dd')
  return releaseDayOf(dateString) <= today
}

export function differenceInDays(date1: string, date2: string): number {
  const timeZone = determineTimeZone(date2)
  const zonedDate1 = utcToZonedTime(new Date(date1), timeZone)
  const zonedDate2 = utcToZonedTime(new Date(date2), timeZone)

  return Math.floor(
    (zonedDate2.getTime() - zonedDate1.getTime()) / (1000 * 60 * 60 * 24),
  )
}

export function formatRelative(dateString: string): string {
  if (!dateString) return ''
  try {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: ptBR,
    })
  } catch {
    return ''
  }
}

export function addDays(dateString: string, days: number): string {
  const timeZone = determineTimeZone(dateString)
  const zonedDate = utcToZonedTime(new Date(dateString), timeZone)
  zonedDate.setDate(zonedDate.getDate() + days)
  return zonedDate.toISOString()
}

/**
 * Soma dias preservando o instante real, sem passar por utcToZonedTime.
 *
 * `addDays` desloca o horário pelo fuso da máquina antes de serializar, então o
 * resultado dele lido de novo por isReleaseDayReached/formatDateShort acumula o
 * offset — o que faz a data exibida virar o dia seguinte quando o horário
 * original está no fim do dia UTC. Use esta versão quando o valor for comparado
 * ou formatado depois.
 *
 * Lança RangeError se a data for inválida, então valide antes de chamar.
 */
export function addDaysUtc(dateString: string, days: number): string {
  const date = new Date(dateString)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString()
}

export function isValidDateString(value: string | null | undefined): boolean {
  if (!value) return false
  return !Number.isNaN(new Date(value).getTime())
}
